/* =============================================================================
 * Select — the primitive the kit was missing (KGH-795)
 * -----------------------------------------------------------------------------
 * The library had Button, Card, Pill, Skeleton, Avatar, Field, TextInput and
 * TextArea, and no Select. So all 44 dropdowns in the product were hand-rolled,
 * and each one re-invented — or forgot — its own focus, invalid and disabled
 * treatment. That is not a hypothetical: an audit that focused every visible
 * control on the deployed app and diffed the computed style found exactly four
 * controls that painted NOTHING on keyboard focus, and all four were selects
 * (the status filter on /admin/partners and three filters on /admin/users).
 * A missing primitive is why.
 *
 * IT IS A NATIVE <select>, DELIBERATELY. A custom listbox would let us style the
 * open menu, and it would cost the things that matter more: the platform picker
 * on a phone, type-ahead, the screen reader's own dropdown semantics, and every
 * keyboard convention a user already has. Those are not worth a styled popup.
 * What IS styled is the closed control, so it sits beside a TextInput without
 * looking like it came from a different product.
 *
 * NOTHING HERE SETS `outline` INLINE, and that is the whole point. An inline
 * declaration outranks an author stylesheet rule whatever its specificity, so an
 * inline `outline: none` silently kills the global
 * `select:focus-visible { outline: 2px solid var(--aps-focus-ring-color) }` in
 * globals.css — which is the bug that put those four selects on the list. This
 * control wears `.aps-input`, the same class TextInput and TextArea wear, so its
 * focus, invalid and disabled states are the ones the rest of the product
 * already has rather than a second set entering the codebase.
 *
 *   <Field id="user-type" label="User type">
 *     <Select {...fieldAria('user-type')} value={t} onChange={e => setT(e.target.value)}>
 *       <option value="">All</option>
 *       <option value="athlete">Athlete</option>
 *     </Select>
 *   </Field>
 * ========================================================================== */
import React from 'react';
import { ChevronDown } from 'react-feather';
import { font, radius } from '../../styles/tokens';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Draws the danger border. Set it from the same message you pass to Field. */
  invalid?: boolean;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ invalid, className, style, children, ...rest }, ref) => (
    /* The wrapper exists only to hang the chevron on. It is inline-flex and
     * width:100% so a Select fills its Field exactly as a TextInput does; a
     * caller wanting a narrow one caps the Field, not this. */
    <span style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
      <select
        ref={ref}
        className={['aps-input', className].filter(Boolean).join(' ')}
        aria-invalid={invalid || undefined}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          /* Right padding clears the chevron. Left matches TextInput so labels
           * line up when the two sit in the same row. */
          padding: '12px 38px 12px 14px',
          fontFamily: font.sans,
          fontSize: font.size.body,
          /* Same cascade trap TextInput documents: `.aps-input:disabled` in
           * globals.css owns the grey fill and muted ink, and an inline pair
           * would outrank it, so a disabled select would look live. */
          color: rest.disabled ? 'var(--aps-text-muted)' : 'var(--aps-ink)',
          background: rest.disabled ? 'var(--aps-fill)' : 'var(--aps-surface)',
          borderRadius: radius.md,
          transition: 'border-color var(--aps-dur-fast) var(--aps-ease-out)',
          /* The platform chevron goes so ours can sit where TextInput's icons
           * do. -webkit- and -moz- are still required for Safari and Firefox. */
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          cursor: rest.disabled ? 'not-allowed' : 'pointer',
          ...style,
        }}
        {...rest}
      >
        {children}
      </select>
      <ChevronDown
        size={16}
        aria-hidden="true"
        /* style, not the `color` prop: react-feather forwards `color` to the SVG
         * stroke attribute, and a var() there resolves to nothing, so the icon
         * disappears. The whole product hits this once and then never again. */
        style={{
          position: 'absolute',
          right: 14,
          color: rest.disabled ? 'var(--aps-text-subtle)' : 'var(--aps-text-2)',
          pointerEvents: 'none',
        }}
      />
    </span>
  )
);

Select.displayName = 'Select';

export default Select;
